import { useState, useEffect } from 'react';
import axios from 'axios';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Dashboard from './Dashboard';
import AdminProducts from './AdminProducts';
import AdminCategories from './AdminCategories';
import AdminServices from './AdminServices';
import AdminMessages from './AdminMessages';
import AdminSettings from './AdminSettings';
import {
  FaTachometerAlt, FaBox, FaTags, FaTools, FaEnvelope, FaCog,
  FaSignOutAlt, FaBars, FaExternalLinkAlt
} from 'react-icons/fa';

const nav = [
  { path: '/yonetim/dashboard', label: 'Dashboard', icon: FaTachometerAlt },
  { path: '/yonetim/urunler', label: 'Ürünler', icon: FaBox },
  { path: '/yonetim/kategoriler', label: 'Kategoriler', icon: FaTags },
  { path: '/yonetim/hizmetler', label: 'Hizmetler', icon: FaTools },
  { path: '/yonetim/mesajlar', label: 'Mesajlar', icon: FaEnvelope },
  { path: '/yonetim/ayarlar', label: 'Ayarlar', icon: FaCog },
];

export default function AdminLayout() {
  const { logout, username } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [logoHeight, setLogoHeight] = useState(36);

  useEffect(() => {
    axios.get('/api/settings').then(r => {
      if (r.data.logo_url) setLogoUrl(r.data.logo_url);
      if (r.data.logo_height) setLogoHeight(Number(r.data.logo_height));
    }).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/yonetim/giris'); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-white/10">
        <img src={logoUrl} alt="Polikap" style={{ height: `${logoHeight}px`, maxWidth: '160px' }} className="w-auto object-contain mb-2" />
        <div className="text-gray-400 text-xs">{username}</div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {nav.map(item => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
          >
            <item.icon size={16} /> {item.label}
          </Link>
        ))}
        <div className="pt-2 border-t border-white/10 mt-2">
          <Link to="/" target="_blank"
            className="sidebar-link text-sm">
            <FaExternalLinkAlt size={13} /> Siteyi Gör
          </Link>
        </div>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        <button onClick={handleLogout}
          className="sidebar-link w-full text-red-400 hover:bg-red-500/20 hover:text-red-300">
          <FaSignOutAlt size={16} /> Çıkış Yap
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 bg-primary flex-col flex-shrink-0 shadow-xl">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-60 bg-primary flex flex-col shadow-xl">
            <Sidebar />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-4 sm:px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-700">
            <FaBars size={20} />
          </button>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-700">
              {nav.find(n => n.path === location.pathname)?.label || 'Admin Panel'}
            </h2>
          </div>
          <button onClick={handleLogout} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5">
            <FaSignOutAlt size={13} /> Çıkış
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <Routes>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="urunler" element={<AdminProducts />} />
            <Route path="kategoriler" element={<AdminCategories />} />
            <Route path="hizmetler" element={<AdminServices />} />
            <Route path="mesajlar" element={<AdminMessages />} />
            <Route path="ayarlar" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
